import React from 'react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  ChoiceField,
  makeReadField,
  refFieldsOf,
  useDependsOn,
  useDroppedRowsWarning,
  validKeysOf,
} from '@ns/plugin-picker-shared';

import { MatchAuthorError } from './match';
import { queryChoiceState } from './query-state';
import {
  AZURE_PICKER_API_VERSION,
  buildSubscriptionQuery,
  selectSubscriptions,
  type SubscriptionPickerUiOptions,
  subscriptionQueryEnabled,
} from './subscription-query';
import type { AzureSubscription } from './types';
import { useQueryResources } from './use-query-resources';

/**
 * Picks one of the subscriptions the user has access to.
 *
 * @remarks
 * Depends on no other field, so it fetches as soon as it renders.
 */
export function AzureSubscriptionPickerComponent(
  props: FieldExtensionComponentProps<
    AzureSubscription,
    SubscriptionPickerUiOptions
  >,
): React.ReactElement {
  const filter = props.uiSchema['ui:options']?.filter;

  // The fields this filter references, in two sets: `waitFor` becomes the
  // dependency (an optional reference opts out of waiting), while `all` is the
  // memo key, so the query is rebuilt when an optional value arrives too.
  const { all, waitFor } = React.useMemo(() => refFieldsOf(filter), [filter]);
  const state = useDependsOn(props, waitFor);

  // `formContext.formData` is the whole form; `props.formData` would be this
  // field's own value. The picker has `rootSchema`, so a name outside the form
  // is a typo rather than an untouched field (`author-error`).
  const validKeys = validKeysOf(props);
  const allFormData = props.formContext?.formData ?? {};

  // An author error in `filter` is reported, not thrown: throwing here tears
  // down the whole form, so the filler gets a blank page and nothing says which
  // template is wrong. It becomes a fatal `failed` state,
  // because a template does not repair itself.
  const refKey = JSON.stringify(
    [...all].sort().map(field => [field, allFormData[field]]), // NOSONAR: sorts strings (field names); code-unit order is intended
  );
  const { authorError, query } = React.useMemo(() => {
    try {
      const readField = makeReadField(allFormData, validKeys, 'author-error');
      return {
        authorError: undefined,
        query: buildSubscriptionQuery(readField, filter),
      };
    } catch (error) {
      // Only an author mistake becomes a message for the author. Anything else
      // is our bug, and it travels on to the error boundary rather than
      // reaching the template writer as advice he cannot act on.
      if (error instanceof MatchAuthorError) {
        return { authorError: error.message, query: '' };
      }
      throw error;
    }
    // `refKey` carries the referenced values, so the query follows them.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, refKey]);

  // A reference that has not arrived yet must not produce a list: an empty list
  // reads as "there is nothing" while the filler has simply not chosen yet
  //.
  const enabled =
    authorError === undefined &&
    state.kind !== 'awaiting' &&
    subscriptionQueryEnabled();

  const subscriptions = useQueryResources(
    { apiVersion: AZURE_PICKER_API_VERSION, query },
    { enabled, select: selectSubscriptions },
  );
  const answer = subscriptions.data;
  useDroppedRowsWarning(
    'Azure subscriptions',
    answer?.droppedRowCount,
    subscriptions.dataUpdatedAt,
  );

  const selected = props.formData;
  const selectedKey =
    typeof selected?.id === 'string' ? selected.id.toLowerCase() : null;

  return (
    <ChoiceField<AzureSubscription>
      description={props.schema.description}
      emptyText="No subscriptions"
      getKey={item => item.id.toLowerCase()}
      getLabel={item => item.name}
      items={answer?.items}
      label={props.schema.title ?? props.name}
      onSelect={item => props.onChange(item)}
      required={props.required}
      selectedKey={selectedKey}
      selectedLabel={typeof selected?.name === 'string' ? selected.name : null}
      sourceKey={query}
      state={queryChoiceState({
        authorError,
        droppedRowCount: answer?.droppedRowCount,
        error: subscriptions.error,
        hasItems: (answer?.items.length ?? 0) > 0,
        isError: subscriptions.isError,
        isPending: enabled && subscriptions.isPending,
        page: answer?.page,
        required: props.required,
        retry: () => void subscriptions.refetch(),
      })}
    />
  );
}
